import React, { useState, useEffect, useCallback } from "react";
import { 
  Grid, 
  CardHeader, 
  CardContent, 
  Box, 
  TextField, 
  MenuItem, 
  InputLabel, 
  FormControl, 
  Select, 
  IconButton, 
  Button, 
  FormHelperText, 
  Chip,
  Paper,
  Typography,
  Stack,
  Avatar,
  Container,
  Fade,
  Divider,
  Card,
  CardActions,
  Alert,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Zoom
} from "@mui/material";
import { 
  AddCircleOutline, 
  Delete, 
  EmojiEvents, 
  ArrowBack,
  Upload,
  AttachMoney,
  CardGiftcard,
  CalendarToday,
  Description,
  Title,
  Category,
  Group
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import LoadingButton from "@mui/lab/LoadingButton";
import { Helmet } from "react-helmet-async";
import { Form, FormikProvider, useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CardLayout from "../../layouts/CardLayout";
import errorHandler from "../../utils/errorHandler";
import { formatPrice } from "../../utils/formatNumber";
import { apiJackpotService } from "../../services/AllServices";
import CloseIcon from "@mui/icons-material/Close";
import { JackpotSchema } from "../../utils/Schema";
import { Autocomplete } from "@mui/material";

function AddEditJackpot() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [contestList, setContestList] = useState([]);
  const [images, setImages] = useState([]);
  const [imageTitles, setImageTitles] = useState([]);
  const [imageNames, setImageNames] = useState([]);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await apiJackpotService.getById({ id });
        setData(response?.data?.data);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    const fetchContest = async () => {
      try {
        const response = await apiJackpotService.getContest();

        const contests = response?.data?.data?.contests || [];
        setContestList(contests);
      } catch (error) {
        errorHandler(error);
      }
    };

    fetchContest();
  }, []);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: JackpotSchema,
    initialValues: {
      name: data?.name || "",
      description: data?.description || "",
      drawDate: data?.drawDate?.slice(0, 10) || "",
      // drawDate: data?.drawDate ? new Date(data.drawDate) : null,
      // winDate: data?.winDate?.slice(0, 10) || "",
      prizeType: data?.prizeType || "",
      items: data?.prizeProduct?.length
        ? data.prizeProduct.map(p => ({ item: p.prizeName || "", image: p.prizeImages?.[0] || null }))
        : [{ item: "", image: null }],
      money: data?.prizeMoney?.length
        ? data.prizeMoney.map(m => ({ amount: m.prizeAmount || "" }))
        : [{ amount: "" }],
      contestId: data?.contestId?.map(c => c._id) || [],
      status: data?.status || "ongoing",
    },

    onSubmit: async (values) => {
      setLoading(true);
      try {
        const formData = new FormData();

        if (id) formData.append("id", id);
        formData.append("name", values.name.trim());
        formData.append("description", values.description.trim());
        formData.append("drawDate", values.drawDate);
        // formData.append("drawDate", moment(values.drawDate).format("YYYY-MM-DD"));
        // formData.append("winDate", values.winDate);
        formData.append("status", values.status);
        formData.append("prizeType", values.prizeType);
        formData.append("imagePath", "jackpot");

        values.contestId.forEach((contestId, index) => {
          formData.append(`contestId[${index}][id]`, contestId);
        });

        if (values.prizeType === "product" && images.length > 0) {
          images.forEach((image, index) => {
            formData.append(`prizeProduct[${index}][prizeImages]`, image);
            formData.append(`prizeProduct[${index}][prizeName]`, imageNames[index]);
          });
        }

        if (values.prizeType === "money" && values.money.length > 0) {
          values.money.forEach((moneyEntry, index) => {
            formData.append(`prizeMoney[${index}][prizeAmount]`, moneyEntry.amount);
          });
        }

        const response = id
          ? await apiJackpotService.edit(formData)
          : await apiJackpotService.add(formData);

        toast.success(response?.data?.message, { toastId: "success" });
        navigate(-1);
      } catch (error) {
        console.error("Submit error:", error);
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }


  });


  const { values, handleChange, handleSubmit, touched, errors, handleBlur } = formik;

  const handleImageChange = (event) => {
    const input = event?.target;
    if (!input || !input.files || input.files.length === 0) return;

    const file = input.files[0];
    const title = input.title || "Untitled Image";
    const name = input.name || "Untitled Image Name";

    setImages((prevImages) => [...prevImages, file]);
    setImageTitles((prevTitles) => [...prevTitles, title]);
    setImageNames((prevNames) => [...prevNames, name]);

    // Also update Formik's imageNames
    formik.setFieldValue("imageNames", [...(formik.values.imageNames || []), ""]);
  };



  const handleRemoveImage = (index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    const updatedTitles = imageTitles.filter((_, i) => i !== index);
    const updatedNames = imageNames.filter((_, i) => i !== index);
    setImages(updatedImages);
    setImageTitles(updatedTitles);
    setImageNames(updatedNames);
  };

  const handleImageClick = (image, index) => {
    setSelectedImage(image);
    setSelectedImageIndex(index);
    setImageModalOpen(true);
  };

  const handleCloseImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage(null);
    setSelectedImageIndex(0);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Helmet>
        <title>{id ? "Edit" : "Add"} Jackpot</title>
      </Helmet>
      <Fade in timeout={500}>
        <Stack spacing={4}>
          {/* Header Section */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
              border: '1px solid',
              borderColor: 'divider',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #00a1dd 0%, #42a5f5 50%, #90caf9 100%)',
              }
            }}
          >
            <Stack direction="row" alignItems="center" spacing={3}>
              <IconButton
                onClick={() => navigate(-1)}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  }
                }}
              >
                <ArrowBack />
              </IconButton>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                  boxShadow: '0 8px 32px rgba(0, 161, 221, 0.4)'
                }}
              >
                <EmojiEvents sx={{ fontSize: 32, color: 'white' }} />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                  {id ? "Edit Jackpot" : "Add New Jackpot"}
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {id ? "Update jackpot details and settings" : "Create a new jackpot with prizes and contests"}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Main Form */}
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Grid container spacing={4}>
                {/* Basic Information Section */}
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <Avatar sx={{ backgroundColor: 'primary.main' }}>
                        <Title />
                      </Avatar>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        Basic Information
                      </Typography>
                    </Stack>
                    
              <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Jackpot Name"
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name && errors.name}
                          required
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmojiEvents sx={{ color: 'text.secondary' }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiInputBase-root': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: 2,
                            },
                            '& .MuiInputBase-input': {
                              color: 'white',
                            },
                            '& .MuiInputLabel-root': {
                              color: 'text.secondary',
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: 'primary.main',
                            }
                          }}
                        />
                      </Grid>

                <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          label="Description"
                          name="description"
                          value={values.description}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.description && Boolean(errors.description)}
                          helperText={touched.description && errors.description}
                          required
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                <Description sx={{ color: 'text.secondary' }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiInputBase-root': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: 2,
                            },
                            '& .MuiInputBase-input': {
                              color: 'white',
                            },
                            '& .MuiInputLabel-root': {
                              color: 'text.secondary',
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: 'primary.main',
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Date and Contest Section */}
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <Avatar sx={{ backgroundColor: 'secondary.main' }}>
                        <CalendarToday />
                      </Avatar>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        Schedule & Contest
                      </Typography>
                    </Stack>
                    
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Draw Date"
                          name="drawDate"
                          format="dd/MM/yyyy" 
                          value={new Date(formik.values.drawDate)}
                          onChange={(date) => formik.setFieldValue("drawDate", date)}
                          renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                error={touched.drawDate && Boolean(errors.drawDate)}
                                required
                                sx={{
                                  '& .MuiInputBase-root': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                    borderRadius: 2,
                                  },
                                  '& .MuiInputBase-input': {
                                    color: 'white',
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: 'text.secondary',
                                  },
                                  '& .MuiInputLabel-root.Mui-focused': {
                                    color: 'primary.main',
                                  }
                                }}
                              />
                            )}
                          />
                        </LocalizationProvider>
                        {touched.drawDate && errors.drawDate && (
                          <FormHelperText error sx={{ mt: 1 }}>{errors.drawDate}</FormHelperText>
                        )}
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth error={touched.status && Boolean(errors.status)}>
                          <InputLabel id="status" sx={{ color: 'text.secondary' }}>Status</InputLabel>
                          <Select
                            labelId="status"
                            label="Status"
                            name="status"
                            value={values.status}   
                            onChange={handleChange}
                            onBlur={handleBlur}
                            sx={{
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: 2,
                              '& .MuiSelect-select': {
                                color: 'white',
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'divider',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                              }
                            }}
                          >
                            <MenuItem value="ongoing">Ongoing</MenuItem>
                            {(id && values.status === "announced") && (
                              <MenuItem value="announced">Announced</MenuItem>
                            )}
                          </Select>
                          {touched.status && errors.status && (
                            <FormHelperText>{errors.status}</FormHelperText>
                          )}
                        </FormControl>
                </Grid>

                      <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      id="contestId"
                      options={contestList || []}
                      getOptionLabel={(option) => option.contestName || ""}
                      value={contestList.filter((item) => values.contestId.includes(item._id))}
                      onChange={(event, newValue) => {
                        const selectedIds = newValue.map((item) => item._id);
                        formik.setFieldValue("contestId", selectedIds);
                      }}
                      onBlur={handleBlur}
                      isOptionEqualToValue={(option, value) => option._id === value._id}
                      noOptionsText="No contests available"
                      loadingText="Loading contests..."
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={option._id}
                            label={option.contestName}
                            {...getTagProps({ index })}
                            size="medium"
                            onDelete={() => {
                              const newValue = value.filter((_, i) => i !== index);
                              const selectedIds = newValue.map((item) => item._id);
                              formik.setFieldValue("contestId", selectedIds);
                            }}
                            sx={{
                              backgroundColor: 'primary.main',
                              color: 'white',
                              fontWeight: 600,
                              '& .MuiChip-deleteIcon': {
                                color: 'white',
                                '&:hover': {
                                  color: 'rgba(255, 255, 255, 0.8)',
                                }
                              },
                              '&:hover': {
                                backgroundColor: 'primary.dark',
                              }
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Contests"
                          placeholder="Type to search and select contests..."
                          name="contestId"
                          error={touched.contestId && Boolean(errors.contestId)}
                          helperText={touched.contestId && errors.contestId}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <Group sx={{ color: 'text.secondary' }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiInputBase-root': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: 2,
                              minHeight: '56px',
                            },
                            '& .MuiInputBase-input': {
                              color: 'white',
                              '&::placeholder': {
                                color: 'rgba(255, 255, 255, 0.5)',
                                opacity: 1,
                              },
                            },
                            '& .MuiInputLabel-root': {
                              color: 'text.secondary',
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: 'primary.main',
                            }
                          }}
                        />
                      )}
                    />
                    
                    {/* Selected Contests Display */}
                    {values.contestId && values.contestId.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 600 }}>
                          Selected Contests ({values.contestId.length}):
                        </Typography>
                        <Paper
                          sx={{
                            p: 2,
                            backgroundColor: 'rgba(0, 161, 221, 0.05)',
                            border: '1px solid',
                            borderColor: 'primary.main',
                            borderRadius: 2,
                          }}
                        >
                          <Grid container spacing={1}>
                            {values.contestId.map((contestId, index) => {
                              const contest = contestList.find(c => c._id === contestId);
                              return contest ? (
                                <Grid item key={contestId}>
                                  <Chip
                                    label={contest.contestName}
                                    size="small"
                                    onDelete={() => {
                                      const newContestIds = values.contestId.filter(id => id !== contestId);
                                      formik.setFieldValue("contestId", newContestIds);
                                    }}
                                    sx={{
                                      backgroundColor: 'primary.main',
                                      color: 'white',
                                      fontWeight: 500,
                                      '& .MuiChip-deleteIcon': {
                                        color: 'white',
                                        '&:hover': {
                                          color: 'rgba(255, 255, 255, 0.8)',
                                        }
                                      },
                                      '&:hover': {
                                        backgroundColor: 'primary.dark',
                                      }
                                    }}
                                  />
                                </Grid>
                              ) : null;
                            })}
                </Grid>
                        </Paper>
                      </Box>
                    )}
                </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Prize Configuration Section */}
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <Avatar sx={{ backgroundColor: 'success.main' }}>
                        <Category />
                      </Avatar>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        Prize Configuration
                      </Typography>
                    </Stack>
                    
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                  <FormControl fullWidth error={touched.prizeType && Boolean(errors.prizeType)}>
                          <InputLabel id="prizeType-label" sx={{ color: 'text.secondary' }}>Prize Type</InputLabel>
                    <Select
                      labelId="prizeType-label"
                      name="prizeType"
                      value={values.prizeType}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Prize Type"
                            sx={{
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: 2,
                              '& .MuiSelect-select': {
                                color: 'white',
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'divider',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                              }
                            }}
                          >
                            <MenuItem value="money">
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <AttachMoney />
                                <Typography>Money Prize</Typography>
                              </Stack>
                            </MenuItem>
                            <MenuItem value="product">
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <CardGiftcard />
                                <Typography>Product Prize</Typography>
                              </Stack>
                            </MenuItem>
                    </Select>
                    {touched.prizeType && errors.prizeType && (
                      <FormHelperText>{errors.prizeType}</FormHelperText>
                    )}
                  </FormControl>
                      </Grid>

                      {/* Money Prize Section */}
                  {values.prizeType === "money" && (
                    <Grid item xs={12}>
                          <Alert 
                            severity="info" 
                            sx={{ 
                              mb: 3, 
                              backgroundColor: 'rgba(33, 150, 243, 0.1)', 
                              color: 'white',
                              borderRadius: 2,
                              border: '1px solid rgba(33, 150, 243, 0.2)',
                              '& .MuiAlert-icon': {
                                color: '#2196f3'
                              }
                            }}
                          >
                            Add multiple money prizes for different winner positions
                          </Alert>
                          
                          {values.money.length === 0 ? (
                            <Box
                              sx={{
                                textAlign: 'center',
                                py: 6,
                                px: 4,
                                border: '2px dashed',
                                borderColor: 'divider',
                                borderRadius: 3,
                                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  borderColor: 'primary.main',
                                  backgroundColor: 'rgba(0, 161, 221, 0.05)',
                                }
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: 80,
                                  height: 80,
                                  mx: 'auto',
                                  mb: 3,
                                  background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
                                  boxShadow: '0 8px 32px rgba(76, 175, 80, 0.3)'
                                }}
                              >
                                <AttachMoney sx={{ fontSize: 40 }} />
                              </Avatar>
                              <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                                No money prizes added yet
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Click the button below to add your first money prize
                              </Typography>
                              <Button
                                variant="contained"
                                startIcon={<AddCircleOutline />}
                                onClick={() => {
                                  const updated = [...values.money, { amount: "" }];
                                  formik.setFieldValue("money", updated);
                                }}
                                sx={{
                                  background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
                                  boxShadow: '0 8px 32px rgba(76, 175, 80, 0.3)',
                                  '&:hover': {
                                    background: 'linear-gradient(135deg, #45a049 0%, #7cb342 100%)',
                                    boxShadow: '0 12px 40px rgba(76, 175, 80, 0.4)',
                                    transform: 'translateY(-2px)',
                                  }
                                }}
                              >
                                Add First Money Prize
                              </Button>
                            </Box>
                          ) : (
                            <Grid container spacing={3}>
                      {values.money.map((moneyObj, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                  <Card
                                    sx={{
                                      height: '100%',
                                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      borderRadius: 3,
                                      overflow: 'hidden',
                                      transition: 'all 0.3s ease',
                                      '&:hover': {
                                        borderColor: 'success.main',
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 12px 40px rgba(76, 175, 80, 0.2)',
                                      }
                                    }}
                                  >
                                    {/* Prize Header */}
                                    <Box
                                      sx={{
                                        p: 2,
                                        background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(139, 195, 74, 0.1) 100%)',
                                        borderBottom: '1px solid',
                                        borderColor: 'divider'
                                      }}
                                    >
                                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                          <Avatar
                                            sx={{
                                              width: 32,
                                              height: 32,
                                              backgroundColor: 'success.main',
                                              boxShadow: '0 4px 16px rgba(76, 175, 80, 0.3)'
                                            }}
                                          >
                                            <AttachMoney sx={{ fontSize: 16 }} />
                                          </Avatar>
                                          <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600 }}>
                                            Prize #{index + 1}
                                          </Typography>
                                        </Stack>
                                        {values.money.length > 1 && (
                                          <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => {
                                              const updated = values.money.filter((_, i) => i !== index);
                                              formik.setFieldValue("money", updated);
                                            }}
                                            sx={{
                                              backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                              '&:hover': {
                                                backgroundColor: 'rgba(244, 67, 54, 0.2)',
                                              }
                                            }}
                                          >
                                            <Delete sx={{ fontSize: 16 }} />
                                          </IconButton>
                                        )}
                                      </Stack>
                                    </Box>

                                    {/* Prize Input */}
                                    <CardContent sx={{ p: 3 }}>
                            <TextField
                              fullWidth
                                        label="Prize Amount"
                              name={`money[${index}].amount`}
                              value={moneyObj.amount ? formatPrice(moneyObj.amount) : moneyObj.amount}
                              onChange={(e) => {
                                let value = e.target.value;
                                // Remove commas and other non-numeric characters except decimal point
                                value = value.replace(/[^0-9.]/g, "");
                                // Handle decimal places
                                if (value.indexOf('.') !== -1) {
                                  const parts = value.split('.');
                                  if (parts[1]?.length > 2) {
                                    value = `${parts[0]}.${parts[1].slice(0, 2)}`;
                                  }
                                }
                                formik.setFieldValue(`money[${index}].amount`, value);
                              }}
                              onBlur={handleBlur}
                              type="text"
                              inputMode="decimal"
                              error={Boolean(touched.money?.[index]?.amount && errors.money?.[index]?.amount)}
                              helperText={touched.money?.[index]?.amount && errors.money?.[index]?.amount}
                                        InputProps={{
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              <AttachMoney sx={{ color: 'text.secondary' }} />
                                            </InputAdornment>
                                          ),
                                        }}
                                        sx={{
                                          '& .MuiInputBase-root': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            borderRadius: 2,
                                          },
                                          '& .MuiInputBase-input': {
                                            color: 'white',
                                            fontSize: '1.1rem',
                                            fontWeight: 600,
                                          },
                                          '& .MuiInputLabel-root': {
                                            color: 'text.secondary',
                                          },
                                          '& .MuiInputLabel-root.Mui-focused': {
                                            color: 'success.main',
                                          }
                                        }}
                                      />
                                    </CardContent>
                                  </Card>
                          </Grid>
                              ))}
                            </Grid>
                          )}

                          {/* Add More Money Prizes Button */}
                          {values.money.length > 0 && (
                            <Box sx={{ mt: 3, textAlign: 'center' }}>
                              <Button
                                variant="outlined"
                                startIcon={<AddCircleOutline />}
                                size="large"
                              onClick={() => {
                                const updated = [...values.money, { amount: "" }];
                                formik.setFieldValue("money", updated);
                              }}
                                sx={{
                                  borderColor: 'success.main',
                                  color: 'success.main',
                                  borderRadius: 2,
                                  px: 4,
                                  py: 1.5,
                                  '&:hover': {
                                    borderColor: 'success.dark',
                                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                    transform: 'translateY(-2px)',
                                  }
                                }}
                              >
                                Add Another Money Prize
                              </Button>
                            </Box>
                          )}
                    </Grid>
                  )}

                      {/* Product Prize Section */}
                {values.prizeType === "product" && (
                        <Grid item xs={12}>
                          <Alert 
                            severity="info" 
                            sx={{ 
                              mb: 3, 
                              backgroundColor: 'rgba(33, 150, 243, 0.1)', 
                              color: 'white',
                              borderRadius: 2,
                              border: '1px solid rgba(33, 150, 243, 0.2)',
                              '& .MuiAlert-icon': {
                                color: '#2196f3'
                              }
                            }}
                          >
                            Upload product images and set their names for each prize
                          </Alert>
                          
                          {images.length === 0 ? (
                            <Box
                              sx={{
                                textAlign: 'center',
                                py: 6,
                                px: 4,
                                border: '2px dashed',
                                borderColor: 'divider',
                                borderRadius: 3,
                                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  borderColor: 'primary.main',
                                  backgroundColor: 'rgba(0, 161, 221, 0.05)',
                                }
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: 80,
                                  height: 80,
                                  mx: 'auto',
                                  mb: 3,
                                  background: 'linear-gradient(135deg, #9c27b0 0%, #e91e63 100%)',
                                  boxShadow: '0 8px 32px rgba(156, 39, 176, 0.3)'
                                }}
                              >
                                <CardGiftcard sx={{ fontSize: 40 }} />
                              </Avatar>
                              <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                                No product images added yet
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Click the button below to add your first product prize
                              </Typography>
                              <input
                                accept="image/*"
                                id="image-upload-empty"
                                type="file"
                                style={{ display: "none" }}
                                onChange={handleImageChange}
                                title="New Image"
                                name="Image Name"
                              />
                              <label htmlFor="image-upload-empty">
                                <Button
                                  variant="contained"
                                  component="span"
                                  startIcon={<Upload />}
                                  sx={{
                                    background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                                    boxShadow: '0 8px 32px rgba(0, 161, 221, 0.3)',
                                    '&:hover': {
                                      background: 'linear-gradient(135deg, #0091c8 0%, #3b8fd9 100%)',
                                      boxShadow: '0 12px 40px rgba(0, 161, 221, 0.4)',
                                      transform: 'translateY(-2px)',
                                    }
                                  }}
                                >
                                  Add First Product Image
                                </Button>
                              </label>
                            </Box>
                          ) : (
                            <Grid container spacing={3}>
                    {images.map((image, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                  <Card
                                    sx={{
                                      height: '100%',
                                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      borderRadius: 3,
                                      overflow: 'hidden',
                                      transition: 'all 0.3s ease',
                                      '&:hover': {
                                        borderColor: 'primary.main',
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 12px 40px rgba(0, 161, 221, 0.2)',
                                      }
                                    }}
                                  >
                                    {/* Image Preview */}
                                    <Box
                                      sx={{
                                        position: 'relative',
                                        height: 200,
                                        overflow: 'hidden',
                                        background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.05) 100%)',
                                        cursor: 'pointer',
                                        '&:hover': {
                                          '& .image-overlay': {
                                            opacity: 1,
                                          }
                                        }
                                      }}
                                      onClick={() => handleImageClick(image, index)}
                                    >
                                      <img
                                        src={URL.createObjectURL(image)}
                                        alt={`Product ${index + 1}`}
                                        style={{ 
                                          width: "100%", 
                                          height: "100%", 
                                          objectFit: "cover",
                                          transition: 'transform 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.transform = 'scale(1.05)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.transform = 'scale(1)';
                                        }}
                                      />
                                      {/* Click to view overlay */}
                                      <Box
                                        className="image-overlay"
                                        sx={{
                                          position: 'absolute',
                                          top: 0,
                                          left: 0,
                                          right: 0,
                                          bottom: 0,
                                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          opacity: 0,
                                          transition: 'opacity 0.3s ease',
                                          cursor: 'pointer'
                                        }}
                                      >
                                        <Typography
                                          variant="h6"
                                          sx={{
                                            color: 'white',
                                            fontWeight: 600,
                                            textAlign: 'center',
                                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)'
                                          }}
                                        >
                                          Click to View
                                        </Typography>
                                      </Box>
                                      {/* Overlay with remove button */}
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          top: 8,
                                          right: 8,
                                          opacity: 0,
                                          transition: 'opacity 0.3s ease',
                                          '&:hover': {
                                            opacity: 1
                                          }
                                        }}
                                      >
                                        <IconButton
                                          size="small"
                                          onClick={() => handleRemoveImage(index)}
                                          sx={{
                                            backgroundColor: 'rgba(244, 67, 54, 0.9)',
                                            color: 'white',
                                            '&:hover': {
                                              backgroundColor: 'rgba(244, 67, 54, 1)',
                                            }
                                          }}
                                        >
                                          <Delete sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Box>
                                    </Box>

                                    {/* Product Details */}
                                    <CardContent sx={{ p: 3 }}>
                          <TextField
                            fullWidth
                                        label={`Product Name ${index + 1}`}
                            name={`imageNames[${index}]`}
                            value={values.imageNames[index]}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={Boolean(touched.imageNames?.[index] && errors.imageNames?.[index])}
                            helperText={touched.imageNames?.[index] && errors.imageNames?.[index]}
                                        InputProps={{
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              <CardGiftcard sx={{ color: 'text.secondary' }} />
                                            </InputAdornment>
                                          ),
                                        }}
                                        sx={{
                                          '& .MuiInputBase-root': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            borderRadius: 2,
                                          },
                                          '& .MuiInputBase-input': {
                                            color: 'white',
                                          },
                                          '& .MuiInputLabel-root': {
                                            color: 'text.secondary',
                                          },
                                          '& .MuiInputLabel-root.Mui-focused': {
                                            color: 'primary.main',
                                          }
                                        }}
                                      />
                                    </CardContent>

                                    {/* Action Buttons */}
                                    <CardActions sx={{ p: 2, pt: 0 }}>
                                      <Button
                                        fullWidth
                                        variant="outlined"
                                        color="error"
                                        startIcon={<Delete />}
                                        onClick={() => handleRemoveImage(index)}
                                        sx={{
                                          borderColor: 'error.main',
                                          color: 'error.main',
                                          '&:hover': {
                                            borderColor: 'error.dark',
                                            backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                          }
                                        }}
                                      >
                                        Remove Product
                          </Button>
                                    </CardActions>
                                  </Card>
                      </Grid>
                    ))}
                            </Grid>
                          )}

                          {/* Add More Images Button */}
                          {images.length > 0 && (
                            <Box sx={{ mt: 3, textAlign: 'center' }}>
                      <input
                        accept="image/*"
                        id="image-upload"
                        type="file"
                        style={{ display: "none" }}
                        onChange={handleImageChange}
                        title="New Image"
                        name="Image Name"
                      />
                      <label htmlFor="image-upload">
                                <Button
                                  variant="outlined"
                                  component="span"
                                  startIcon={<Upload />}
                                  size="large"
                                  sx={{
                                    borderColor: 'primary.main',
                                    color: 'primary.main',
                                    borderRadius: 2,
                                    px: 4,
                                    py: 1.5,
                                    '&:hover': {
                                      borderColor: 'primary.dark',
                                      backgroundColor: 'rgba(0, 161, 221, 0.1)',
                                      transform: 'translateY(-2px)',
                                    }
                                  }}
                                >
                                  Add Another Product Image
                        </Button>
                      </label>
                            </Box>
                          )}
                    </Grid>
                )}
                    </Grid>
                  </Paper>
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        onClick={() => navigate(-1)}
                        sx={{
                          borderColor: 'divider',
                          color: 'text.secondary',
                          '&:hover': {
                            borderColor: 'primary.main',
                            color: 'primary.main',
                          }
                        }}
                      >
                        Cancel
                      </Button>
                      <LoadingButton
                        type="submit"
                        variant="contained"
                        loading={loading}
                        sx={{
                          background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                          boxShadow: '0 8px 32px rgba(0, 161, 221, 0.3)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #0091c8 0%, #3b8fd9 100%)',
                            boxShadow: '0 12px 40px rgba(0, 161, 221, 0.4)',
                            transform: 'translateY(-2px)',
                          }
                        }}
                      >
                        {id ? "Update Jackpot" : "Create Jackpot"}
                  </LoadingButton>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </Stack>
      </Fade>

      {/* Image View Modal */}
      <Dialog
        open={imageModalOpen}
        onClose={handleCloseImageModal}
        maxWidth="md"
        fullWidth
        TransitionComponent={Zoom}
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            borderRadius: 3,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant="h6">
            Product Image {selectedImageIndex + 1}
          </Typography>
          <IconButton
            onClick={handleCloseImageModal}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          {selectedImage && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '400px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              }}
            >
              <img
                src={URL.createObjectURL(selectedImage)}
                alt={`Product ${selectedImageIndex + 1}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
                }}
              />
    </Box>
          )}
        </DialogContent>
        
        <DialogActions
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            padding: 2,
            justifyContent: 'center'
          }}
        >
          <Button
            onClick={handleCloseImageModal}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0091c8 0%, #3b8fd9 100%)',
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AddEditJackpot;